import { ReactNode } from "react";

import PersonalApiKeysManagedUsersCallout from "components/ApiKeys/PersonalApiKeys/ManagedUsersCallout";
import { InfoCircle } from "components/icons/generated";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import Callout from "ds/components/Callout";
import FeedbackActions from "ds/components/Feedback/Actions";
import Link from "ds/components/Link";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPagePersonal } from "hooks/useAnalytics/pages/personal";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { SeatLimit } from "types/generated";
import SeatLimitsNotification from "views/Account/Settings/SeatLimitsNotification";
import { SEAT_LIMITS_TOOLTIP } from "views/Account/Settings/SeatLimitsNotification/constants";
import { SeatLimitsNotificationEntity } from "views/Account/Settings/SeatLimitsNotification/types";
import useSeatLimit from "views/Account/Settings/SeatLimitsNotification/useSeats";
import { AccountContext } from "views/AccountWrapper";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";

import {
  MANAGE_API_KEYS_INFO_KEY,
  PERSONAL_API_KEYS_MANAGED_USERS_TOOLTIP,
  PERSONAL_API_KEYS_TURNED_OFF_TOOLTIP,
} from "../../constants";

type PersonalSettingsApiKeysPageLayoutProps = {
  children: ReactNode;
  onOpenCreateApiKeyDrawer?: () => void;
  onOpenInfoDrawer?: () => void;
  seats?: SeatLimit;
  showManagedUsersWarning?: boolean;
  showOrganizationSettingsCallout?: boolean;
  isPersonalApiKeysTurnedOff?: boolean;
  hasWorkingKeys?: boolean;
  hasKeysDisabledByAccount?: boolean;
  hasApiKeys?: boolean;
};

const PersonalSettingsApiKeysPageLayout = ({
  children,
  onOpenCreateApiKeyDrawer,
  onOpenInfoDrawer,
  seats,
  showManagedUsersWarning,
  showOrganizationSettingsCallout,
  isPersonalApiKeysTurnedOff = false,
  hasWorkingKeys = false,
  hasKeysDisabledByAccount = false,
  hasApiKeys = false,
}: PersonalSettingsApiKeysPageLayoutProps) => {
  const { accountName, viewer } = useTypedContext(AccountContext);
  const { personalApiKeys } = useTypedFlags();

  const { isLimitReached } = useSeatLimit(seats);

  const showStateCallout = !showManagedUsersWarning && hasApiKeys && isPersonalApiKeysTurnedOff;

  const createDisabledTooltip =
    (showManagedUsersWarning && PERSONAL_API_KEYS_MANAGED_USERS_TOOLTIP) ||
    (isPersonalApiKeysTurnedOff && PERSONAL_API_KEYS_TURNED_OFF_TOOLTIP) ||
    (isLimitReached && SEAT_LIMITS_TOOLTIP) ||
    undefined;

  const pageTitle = personalApiKeys ? "Personal API keys" : "API keys";

  useTitle(`Personal Settings · ${pageTitle} · ${accountName}`);

  const trackAnalyticsSegmentEvent = useAnalytics({
    page: AnalyticsPagePersonal.PersonalApiKeys,
  });

  return (
    <>
      {showManagedUsersWarning && (
        <PersonalApiKeysManagedUsersCallout analyticsPage={AnalyticsPagePersonal.PersonalApiKeys} />
      )}

      {showStateCallout && hasWorkingKeys && (
        <Callout variant="warning" title="Personal API keys are not turned on for this account">
          An admin turned off personal API keys for this account. The keys you have created will
          continue to work but you are not able to create new ones.
        </Callout>
      )}

      {showStateCallout && !hasWorkingKeys && hasKeysDisabledByAccount && (
        <Callout variant="danger" title="Personal API keys were disabled for this account">
          An organization admin turned off personal API keys and revoked existing keys. Revoked keys
          can’t be used again, but you can delete them.
        </Callout>
      )}

      {showOrganizationSettingsCallout && viewer.admin && (
        <Callout
          variant="info"
          title="Manage API keys in your organization"
          storageKey={MANAGE_API_KEYS_INFO_KEY}
          closeCallback={() => trackAnalyticsSegmentEvent("Organization Settings Callout Close")}
        >
          To manage all API keys created in your organization go to your Organization settings.
          <FeedbackActions>
            <Link
              analyticsPage={AnalyticsPagePersonal.PersonalApiKeys}
              analyticsTitle="Organization Settings Callout Click"
              href="/settings/api-keys"
              size="small"
            >
              Organization settings
            </Link>
          </FeedbackActions>
        </Callout>
      )}

      {seats && !showManagedUsersWarning && (
        <SeatLimitsNotification
          entity={SeatLimitsNotificationEntity.ApiKey}
          seats={seats}
          type="callout"
        />
      )}

      <PageInfoHeaderWrapper>
        <Box direction="row" align="center" gap="medium">
          <PageInfoHeaderTitle title={pageTitle} />
          {personalApiKeys && <Badge variant="blue" type="strong" text="BETA" />}
          {onOpenInfoDrawer && (
            <ButtonIcon onPress={onOpenInfoDrawer} icon={InfoCircle} variant="ghost">
              Open info drawer
            </ButtonIcon>
          )}
        </Box>
        <PageInfoHeaderActions>
          {onOpenCreateApiKeyDrawer && (
            <Tooltip
              active={!!createDisabledTooltip}
              widthMode="maxWidthSm"
              on={(tooltipProps) => (
                <Button
                  onPress={onOpenCreateApiKeyDrawer}
                  variant="primary"
                  disabled={!!createDisabledTooltip}
                  {...tooltipProps}
                >
                  Create API key
                </Button>
              )}
            >
              {createDisabledTooltip}
            </Tooltip>
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      {children}
    </>
  );
};

export default PersonalSettingsApiKeysPageLayout;
